import React from "react";

// Regex that captures URLs (http/https or www.) and leaves the rest of the text intact.
// Stops at whitespace or common end-of-sentence punctuation.
const URL_RE = /\b((?:https?:\/\/|www\.)[^\s<>"]+)/gi;

/** Arabic / Hebrew / Farsi etc. — if the first strong-directional letter is
 *  in these ranges, the text should render RTL. Works for mixed messages
 *  (e.g. Arabic with English URLs). */
function detectDir(s) {
  if (!s) return "ltr";
  // Strong RTL characters: Arabic, Hebrew, Persian, Syriac
  const rtl = /[\u0591-\u07FF\u200F\u202B\u202E\uFB1D-\uFDFD\uFE70-\uFEFC]/;
  return rtl.test(s) ? "rtl" : "ltr";
}

/**
 * Render a block of text with:
 *  - clickable links (opens in new tab, rel=noopener)
 *  - preserved line breaks (whitespace-pre-wrap)
 *  - automatic LTR/RTL direction per line based on first strong character
 *    (so Arabic messages align right, English align left)
 *  - line-level direction lets mixed-language feeds render cleanly
 */
export default function RichText({ text, className = "", testId }) {
  if (!text) return null;
  const lines = text.split("\n");
  return (
    <div className={`whitespace-pre-wrap break-words leading-relaxed ${className}`} data-testid={testId}>
      {lines.map((line, i) => (
        <div key={i} dir={detectDir(line)} style={{ unicodeBidi: "plaintext" }}>
          {renderLineWithLinks(line)}
          {line === "" ? "\u00A0" : null}
        </div>
      ))}
    </div>
  );
}

function renderLineWithLinks(line) {
  if (!line) return null;
  const parts = [];
  let lastIdx = 0;
  let match;
  URL_RE.lastIndex = 0;
  while ((match = URL_RE.exec(line)) !== null) {
    if (match.index > lastIdx) parts.push(line.slice(lastIdx, match.index));
    const raw = match[1];
    const href = raw.startsWith("http") ? raw : `https://${raw}`;
    parts.push(
      <a
        key={`${match.index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="text-[#7C3AED] underline underline-offset-2 hover:text-[#EA580C] break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {raw}
      </a>
    );
    lastIdx = match.index + raw.length;
  }
  if (lastIdx < line.length) parts.push(line.slice(lastIdx));
  return parts;
}

export { detectDir };
